import { useCallback, useEffect, useMemo, useState } from "react"

import {
  DASHBOARD_DRILLDOWN_INSIGHTS_MAX_INPUT_ROWS,
  type DashboardDrilldownInsightRow,
  type DashboardDrilldownInsightsInput,
} from "../llm-generation"
import type { DrillDownRunRecord } from "../widgets/types/core"
import type { UnifiedMetricDrillDownState } from "./useUnifiedMetricDrillDownNavigation"
import { useUnifiedMetricDrillDownRows } from "./useUnifiedMetricDrillDownRows"

interface UseUnifiedMetricDrillDownInsightsInput {
  currentDashboardId: string | null | undefined
  focusedChartTitle: string | null
  onInsightRowOpen?: (
    row: Pick<DrillDownRunRecord, "run_id" | "simulation_output_id">,
  ) => void
  unifiedMetricDrillDown: UnifiedMetricDrillDownState | null
}

export function useUnifiedMetricDrillDownInsights({
  currentDashboardId,
  focusedChartTitle,
  onInsightRowOpen,
  unifiedMetricDrillDown,
}: UseUnifiedMetricDrillDownInsightsInput) {
  const unifiedDrillDownRowsState = useUnifiedMetricDrillDownRows({
    clickData: unifiedMetricDrillDown?.clickData ?? null,
    enabled: !!unifiedMetricDrillDown,
  })
  const [
    focusedInsightSimulationOutputId,
    setFocusedInsightSimulationOutputId,
  ] = useState<string | null>(null)
  const [
    hoveredInsightSimulationOutputId,
    setHoveredInsightSimulationOutputId,
  ] = useState<string | null>(null)

  const unifiedDrillDownSelectionKey = useMemo(() => {
    if (!unifiedMetricDrillDown) {
      return null
    }

    const { clickData, widgetId, metricId } = unifiedMetricDrillDown
    return JSON.stringify({
      widgetId,
      metricId,
      drillDownScope: clickData.drillDownScope,
      bucketTime: clickData.bucketTime,
      endDate: clickData.endDate,
      metricIdFromClick: clickData.metricId,
      splitBy: clickData.splitBy,
      splitValue: clickData.splitValue,
      stringValue: clickData.stringValue,
      aggregatedValue: clickData.aggregatedValue,
    })
  }, [unifiedMetricDrillDown])

  useEffect(() => {
    setFocusedInsightSimulationOutputId(null)
    setHoveredInsightSimulationOutputId(null)
  }, [unifiedDrillDownSelectionKey])

  const dashboardInsightRows = useMemo<DashboardDrilldownInsightRow[]>(
    () =>
      unifiedDrillDownRowsState.rows
        .slice(0, DASHBOARD_DRILLDOWN_INSIGHTS_MAX_INPUT_ROWS)
        .map((row) => ({
          simulationOutputId: row.simulation_output_id,
          runId: row.run_id,
          occurredAt: row.occurred_at,
          ...(row.created_at ? { createdAt: row.created_at } : {}),
          value: row.value,
          metricId: row.metric_id,
          ...(row.agent_id ? { agentId: row.agent_id } : {}),
          ...(row.persona_id ? { personaId: row.persona_id } : {}),
          ...(row.template_name ? { templateName: row.template_name } : {}),
          ...(row.test_set_id ? { testSetId: row.test_set_id } : {}),
        })),
    [unifiedDrillDownRowsState.rows],
  )

  const dashboardInsightChart = useMemo<
    DashboardDrilldownInsightsInput["chart"] | null
  >(() => {
    if (!unifiedMetricDrillDown) {
      return null
    }

    const { clickData, widgetId } = unifiedMetricDrillDown
    return {
      ...(clickData.drillDownScope
        ? { drillDownScope: clickData.drillDownScope }
        : {}),
      ...(currentDashboardId ? { dashboardId: currentDashboardId } : {}),
      widgetId,
      ...(focusedChartTitle ? { title: focusedChartTitle } : {}),
      metricId: clickData.metricId,
      dataSource: clickData.dataSource,
      aggregation: clickData.aggregation,
      metricOutputType: clickData.metricOutputType,
      bucketTime: clickData.bucketTime,
      ...(clickData.endDate ? { endDate: clickData.endDate } : {}),
      ...(clickData.bucketInterval
        ? { bucketInterval: clickData.bucketInterval }
        : {}),
      ...(clickData.splitBy ? { splitBy: clickData.splitBy } : {}),
      ...(clickData.splitValue ? { splitValue: clickData.splitValue } : {}),
      ...(clickData.stringValue ? { stringValue: clickData.stringValue } : {}),
      aggregatedValue: clickData.aggregatedValue,
      displayLabel: clickData.displayLabel,
      timezone: unifiedDrillDownRowsState.timezone,
    }
  }, [
    currentDashboardId,
    focusedChartTitle,
    unifiedDrillDownRowsState.timezone,
    unifiedMetricDrillDown,
  ])

  const rowRefToDrillDownRow = useMemo(
    () =>
      new Map<string, DrillDownRunRecord>(
        unifiedDrillDownRowsState.rows
          .slice(0, DASHBOARD_DRILLDOWN_INSIGHTS_MAX_INPUT_ROWS)
          .map((row, index) => [`row-${index + 1}`, row]),
      ),
    [unifiedDrillDownRowsState.rows],
  )

  const rowRefToSimulationOutputId = useMemo(
    () =>
      new Map(
        Array.from(rowRefToDrillDownRow.entries()).map(([rowRef, row]) => [
          rowRef,
          row.simulation_output_id,
        ]),
      ),
    [rowRefToDrillDownRow],
  )

  const handleInsightEvidenceLinkClick = useCallback(
    (rowRef: string) => {
      const row = rowRefToDrillDownRow.get(rowRef)
      if (!row) {
        return
      }

      setFocusedInsightSimulationOutputId(row.simulation_output_id)
      onInsightRowOpen?.(row)
    },
    [onInsightRowOpen, rowRefToDrillDownRow],
  )

  const handleInsightEvidenceLinkHover = useCallback(
    (rowRef: string | null) => {
      if (!rowRef) {
        setHoveredInsightSimulationOutputId(null)
        return
      }

      setHoveredInsightSimulationOutputId(
        rowRefToSimulationOutputId.get(rowRef) ?? null,
      )
    },
    [rowRefToSimulationOutputId],
  )

  return {
    dashboardInsightChart,
    dashboardInsightRows,
    focusedInsightSimulationOutputId,
    handleInsightEvidenceLinkClick,
    handleInsightEvidenceLinkHover,
    highlightedInsightSimulationOutputId:
      hoveredInsightSimulationOutputId ?? focusedInsightSimulationOutputId,
    setFocusedInsightSimulationOutputId,
    unifiedDrillDownRowsState,
    unifiedDrillDownSelectionKey,
  }
}
